import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Home, Building2, SprayCan, Truck } from "lucide-react";

import { PageHero } from "../components/site/PageHero";
import { usePageMeta } from "../hooks/usePageMeta";

const SERVICES = [
  {
    id: "huishoudelijk",
    icon: Home,
    title: "Huishoudelijke schoonmaak",
    tagline: "Een frisse, hygiënische woning — zonder zorgen.",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1400&q=80",
    description:
      "Wij verzorgen de wekelijkse, tweewekelijkse of eenmalige reiniging van uw woning of appartement. Onze vaste poetshulp kent uw woning en zorgt voor consistentie. We werken met milieuvriendelijke producten en respecteren uw privacy en uw spullen.",
    forWho: [
      "Particulieren met een drukke agenda",
      "Tweeverdieners en gezinnen",
      "Senioren die graag praktische hulp wensen",
      "Eigenaars van vakantiewoningen",
    ],
    includes: [
      "Stoffen, stofzuigen en dweilen",
      "Sanitair (badkamer & toilet)",
      "Keuken (oppervlakken, fornuis, koelkast extern)",
      "Bedden opmaken en strijkservice op aanvraag",
    ],
    priceFrom: "€28 / uur",
  },
  {
    id: "kantoor",
    icon: Building2,
    title: "Kantoorschoonmaak",
    tagline: "Een representatieve, hygiënische werkplek — discreet en stipt.",
    image: "https://customer-assets.emergentagent.com/job_dila-clean/artifacts/cnkdy6qt_4e8ab63f-6940-4147-8ebe-7708a3b62602.jpeg",
    description:
      "Wij houden uw kantoor, praktijk of commerciële ruimte impeccabel — dagelijks, wekelijks of periodiek. Werkzaamheden buiten kantooruren of in de vroege ochtend zijn perfect mogelijk. Vaste contactpersoon, transparante checklists en kwaliteitscontroles.",
    forWho: [
      "Kantoren en co-working spaces",
      "Medische & paramedische praktijken",
      "Winkels, salons en horecazaken",
      "Bedrijfsgebouwen met meerdere vestigingen",
    ],
    includes: [
      "Bureaus, vergaderzalen en gemeenschappelijke ruimtes",
      "Sanitair (dagelijkse opfrissing & wekelijkse desinfectie)",
      "Keuken, koffiehoek en lunchruimte",
      "Vuilnis legen, verbruiksgoederen aanvullen",
    ],
    priceFrom: "Op maat — vanaf €32 / uur",
  },
  {
    id: "diepte",
    icon: SprayCan,
    title: "Dieptereiniging",
    tagline: "De grondige opkuis die er anders nooit van komt.",
    image: "https://customer-assets.emergentagent.com/job_dila-clean/artifacts/g9y7p7f1_2026-05-15%20%282%29.jpg",
    description:
      "Een dieptereiniging is meer dan poetsen: wij pakken kalkaanslag, vetlagen, voegen, gordijnrails, plinten en achter meubels aan. Ideaal als seizoensreiniging, voor een feestelijke gelegenheid of vóór de start van een nieuw schoonmaakcontract.",
    forWho: [
      "Woningen die een grondige opfrisbeurt nodig hebben",
      "Restaurants en keukens (dieptevet & roostervrij)",
      "Airbnb / vakantiewoningen tussen reservaties",
      "Praktijken na ingrijpende werken",
    ],
    includes: [
      "Vetontvetting keuken (oven, dampkap, tegels)",
      "Sanitair: kalk, voegen, douchewanden",
      "Ramen (binnenkant), vensterbanken, rolluiken",
      "Plinten, deurkozijnen, schakelaars",
    ],
    priceFrom: "€35 / uur (forfait mogelijk)",
  },
  {
    id: "verhuis",
    icon: Truck,
    title: "Schoonmaak na verhuis",
    tagline: "Vlekkeloze oplevering — of een frisse start in uw nieuwe woning.",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1400&q=80",
    description:
      "Bij een verhuis is een complete reiniging essentieel: voor de oplevering aan de eigenaar/syndicus, of om in een spotless thuis te kunnen aankomen. Wij werken met een gedetailleerde checklist en garanderen oplevering-conform-staat.",
    forWho: [
      "Huurders bij einde van het huurcontract",
      "Eigenaars die hun pand verkopen",
      "Kopers vóór de intrek",
      "Vastgoedmakelaars en syndici",
    ],
    includes: [
      "Volledig pand binnen (incl. lege kasten en lades)",
      "Sanitair en keuken tot in detail",
      "Ramen binnen + buiten (indien bereikbaar)",
      "Afvoer vuilnis & rest-materiaal",
    ],
    priceFrom: "Forfait op basis van m² — vanaf €180",
  },
];

export default function ServicesPage() {
  usePageMeta({
    title: "Diensten — Schoonmaakdiensten in Antwerpen | Dila Group BV",
    description:
      "Onze 4 schoonmaakdiensten in Antwerpen: huishoudelijke schoonmaak, kantoorschoonmaak, dieptereiniging en schoonmaak na verhuis. Transparante prijzen vanaf €28/uur.",
    path: "/diensten",
  });

  return (
    <>
      <PageHero
        breadcrumb="Diensten"
        eyebrow="Onze Diensten"
        title="Schoonmaak­diensten op maat van uw situatie."
        intro="Vier kerndiensten — uitgevoerd door één betrouwbaar team. Transparante prijzen, een vaste contactpersoon en altijd resultaatgarantie."
      />

      {/* Quick nav */}
      <section className="bg-white border-b border-slate-200 sticky top-20 z-30 backdrop-blur-md bg-white/85">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap gap-2">
          {SERVICES.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`service-anchor-${s.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-[#F4F7F6] hover:bg-[#0284C7] hover:text-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors"
            >
              <s.icon size={14} /> {s.title}
            </a>
          ))}
        </div>
      </section>

      {/* Service blocks */}
      <div className="bg-white">
        {SERVICES.map((s, idx) => (
          <section
            key={s.id}
            id={s.id}
            data-testid={`service-block-${s.id}`}
            className={`py-20 sm:py-28 scroll-mt-32 ${idx % 2 === 1 ? "bg-[#F4F7F6]" : "bg-white"}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className={`grid lg:grid-cols-12 gap-10 lg:gap-14 items-center ${idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}>
                <motion.div
                  initial={{ opacity: 0, x: idx % 2 === 1 ? 24 : -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6 }}
                  className="lg:col-span-7"
                >
                  <div className="relative aspect-[5/4] rounded-3xl overflow-hidden ring-1 ring-slate-200 shadow-xl">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 shadow-md">
                      <s.icon size={16} className="text-[#0284C7]" />
                      <span className="text-xs font-semibold text-[#0F172A]">Dienst {String(idx + 1).padStart(2, "0")}</span>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: idx % 2 === 1 ? -24 : 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6 }}
                  className="lg:col-span-5"
                >
                  <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[#0284C7]">{s.tagline}</span>
                  <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#0F172A] text-balance">
                    {s.title}
                  </h2>
                  <p className="mt-5 text-slate-600 leading-relaxed">{s.description}</p>

                  <div className="mt-7 grid sm:grid-cols-2 gap-5">
                    <div>
                      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">Voor wie</h3>
                      <ul className="space-y-2 text-sm">
                        {s.forWho.map((w) => (
                          <li key={w} className="flex items-start gap-2 text-slate-700">
                            <Check size={14} className="text-[#16A34A] mt-1 shrink-0" strokeWidth={3} />
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">Wat doen we</h3>
                      <ul className="space-y-2 text-sm">
                        {s.includes.map((w) => (
                          <li key={w} className="flex items-start gap-2 text-slate-700">
                            <Check size={14} className="text-[#0284C7] mt-1 shrink-0" strokeWidth={3} />
                            {w}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    <div className="rounded-2xl bg-[#F0FDF4] border border-[#16A34A]/20 px-5 py-3">
                      <p className="text-xs uppercase tracking-widest text-[#16A34A] font-bold">Prijs vanaf</p>
                      <p className="font-display text-xl font-extrabold text-[#0F172A]">{s.priceFrom}</p>
                    </div>
                    <Link
                      to="/contact"
                      data-testid={`service-quote-cta-${s.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-6 py-3.5 shadow-md shadow-sky-200 transition-all hover:-translate-y-0.5"
                    >
                      Offerte aanvragen <ArrowRight size={16} />
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        ))}
      </div>
    </>
  );
}
